import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';
import { TagsField } from '@/features/operations/filters/TagsField';
import { accountFieldLabel, isAccountFieldVisible } from './domain';
import { useGatewayDescriptor } from './useGatewayDescriptors';
import type { AdminGatewayAccount, GatewayAccountStatus } from '@/api/models';

interface GatewayAccountEditDialogProps {
  open: boolean;
  account: AdminGatewayAccount;
  onClose: () => void;
  onSaved: (account: AdminGatewayAccount) => void;
}

const STATUS_VALUES: GatewayAccountStatus[] = ['active', 'suspended'];

/**
 * `PATCH /v1/admin/gateway-accounts/{id}` — every field optional; never
 * accepts a credential field (that only ever flows through `.../secrets`).
 */
export function GatewayAccountEditDialog({ open, account, onClose, onSaved }: GatewayAccountEditDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [alias, setAlias] = useState(account.alias);
  const [capabilities, setCapabilities] = useState<string[]>(account.capabilities);
  const [status, setStatus] = useState<GatewayAccountStatus>(account.status);
  const [externalAccountCode, setExternalAccountCode] = useState(account.externalAccountCode ?? '');
  const [externalTerminalId, setExternalTerminalId] = useState(account.externalTerminalId ?? '');
  const [externalEndpointPrefix, setExternalEndpointPrefix] = useState(account.externalEndpointPrefix ?? '');
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Same descriptor the create dialog follows — a field this gateway never
  // reads is not offered, unless this account already carries a value in it and
  // clearing it must stay possible.
  const { descriptor } = useGatewayDescriptor(account.gateway, open);

  useEffect(() => {
    if (open) {
      setAlias(account.alias);
      setCapabilities(account.capabilities);
      setStatus(account.status);
      setExternalAccountCode(account.externalAccountCode ?? '');
      setExternalTerminalId(account.externalTerminalId ?? '');
      setExternalEndpointPrefix(account.externalEndpointPrefix ?? '');
      setAccessDenied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const updated = await payApiClient.updateAdminGatewayAccount(account.id, {
        alias: alias.trim(),
        capabilities,
        status,
        externalAccountCode: externalAccountCode.trim() || undefined,
        externalTerminalId: externalTerminalId.trim() || undefined,
        externalEndpointPrefix: externalEndpointPrefix.trim() || undefined,
      });
      notify(translate('pay.gatewayAccounts.edit.success'), { type: 'success' });
      onSaved(updated);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.gatewayAccounts.edit.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              size="small"
              label={translate('pay.gatewayAccounts.edit.fieldAlias')}
              value={alias}
              onChange={(event) => setAlias(event.target.value)}
              fullWidth
            />
            <TagsField
              label={translate('pay.gatewayAccounts.edit.fieldCapabilities')}
              helperText={translate('pay.gatewayAccounts.edit.fieldCapabilitiesHelp')}
              values={capabilities}
              onChange={setCapabilities}
            />
            <TextField
              select
              size="small"
              label={translate('pay.gatewayAccounts.edit.fieldStatus')}
              value={status}
              onChange={(event) => setStatus(event.target.value as GatewayAccountStatus)}
              fullWidth
            >
              {STATUS_VALUES.map((value) => (
                <MenuItem key={value} value={value}>
                  {translate(`pay.gatewayAccounts.status.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            {isAccountFieldVisible(descriptor?.accountFields.externalAccountCode, externalAccountCode) && (
              <TextField
                size="small"
                label={accountFieldLabel(translate, account.gateway, 'externalAccountCode', translate('pay.gatewayAccounts.edit.fieldExternalAccountCode'))}
                value={externalAccountCode}
                onChange={(event) => setExternalAccountCode(event.target.value)}
                fullWidth
              />
            )}
            {isAccountFieldVisible(descriptor?.accountFields.externalTerminalId, externalTerminalId) && (
              <TextField
                size="small"
                label={accountFieldLabel(translate, account.gateway, 'externalTerminalId', translate('pay.gatewayAccounts.edit.fieldExternalTerminalId'))}
                value={externalTerminalId}
                onChange={(event) => setExternalTerminalId(event.target.value)}
                fullWidth
              />
            )}
            {isAccountFieldVisible(descriptor?.accountFields.externalEndpointPrefix, externalEndpointPrefix) && (
              <TextField
                size="small"
                label={accountFieldLabel(translate, account.gateway, 'externalEndpointPrefix', translate('pay.gatewayAccounts.edit.fieldExternalEndpointPrefix'))}
                helperText={translate('pay.gatewayAccounts.edit.fieldExternalEndpointPrefixHelp')}
                value={externalEndpointPrefix}
                onChange={(event) => setExternalEndpointPrefix(event.target.value)}
                fullWidth
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('pay.gatewayAccounts.edit.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={alias.trim().length === 0 || submitting}>
            {translate('pay.gatewayAccounts.edit.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
